/** SF-4845: small display helpers for runs and steps. */
import { envLabel } from './constants';
import type { EnvCopyEstimate, EnvCopyFileKind, EnvCopySize } from './api/envCopyClient';

/** "14 Oct 2026, 10:00:05" in the viewer's time zone, or blank when unknown. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** "1 h 2 min", "3 min 5 s", "12 s". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min ${s} s`;
  return `${s} s`;
}

/**
 * How long something took, or has taken so far while it runs (`now` for the running case).
 * Blank when it has not started.
 */
export function durationBetween(
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
  now: number,
): string {
  if (!startedAt) return '';
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : now;
  if (Number.isNaN(start) || Number.isNaN(end)) return '';
  return formatDuration(end - start);
}

/** "documents copied" from "documents_copied" or "documentsCopied". */
export function humanKey(key: string): string {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Flat key/value pairs in the order the server sent them; nothing when empty. */
export function flatEntries(values: Record<string, unknown> | null | undefined) {
  if (!values || typeof values !== 'object') return [];
  return Object.entries(values)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .map(([k, v]) => ({ key: k, label: humanKey(k), value: String(v) }));
}

/** Who started a run: their name, else their email. */
export const starterName = (by: { name?: string; email?: string } | null | undefined) =>
  by?.name || by?.email || 'Unknown';

/** SF-5084: "521,838". */
export const formatCount = (n: number) => n.toLocaleString('en-GB');

const oneDecimal = (n: number) =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** SF-5084: "74.5 GB", or "812.3 MB" under 1 GB (decimal units, as the storage totals use). */
export const formatBytes = (bytes: number) =>
  bytes >= 1e9 ? `${oneDecimal(bytes / 1e9)} GB` : `${oneDecimal(bytes / 1e6)} MB`;

/** SF-5084: "1 month", "2 months". */
export const monthsLabel = (n: number) => `${n} ${n === 1 ? 'month' : 'months'}`;

/**
 * SF-5084: what a run was asked to do with images and report files (not whether it has done it:
 * the steps say that). A run from before the choice existed (no copyImages / copyReports) copies
 * every file.
 */
export function filesOptionsSummary(
  options:
    | { copyImages?: unknown; copyReports?: unknown; filesMonths?: unknown }
    | null
    | undefined,
): string {
  if (typeof options?.copyImages !== 'boolean' && typeof options?.copyReports !== 'boolean') {
    return 'All files (older copy)';
  }
  const images = options?.copyImages === true;
  const reports = options?.copyReports === true;
  const asked = `Copy images: ${images ? 'yes' : 'no'}; copy report files: ${reports ? 'yes' : 'no'}`;
  if (!images && !reports) return asked;
  const months = options?.filesMonths;
  return typeof months === 'number' && months > 0
    ? `${asked}; only files from the last ${monthsLabel(months)}`
    : `${asked}; all history`;
}

/** "Production: 521,838 files, 74.5 GB." or "Production: size not known." */
const sideSentence = (env: string, size: EnvCopySize | undefined) => {
  const parts = [
    size?.objects != null ? `${formatCount(size.objects)} files` : null,
    size?.bytes != null ? formatBytes(size.bytes) : null,
  ].filter(Boolean);
  return `${envLabel(env)}: ${parts.length ? parts.join(', ') : 'size not known'}.`;
};

const moreSentence = (bytes: number | null | undefined) => {
  if (bytes == null) return null;
  const note = '(only new or changed files are copied)';
  return bytes > 0
    ? `About ${formatBytes(bytes)} more to copy ${note}.`
    : `Nothing more to copy by size ${note}.`;
};

/** The estimate line for one kind, or why there is none. */
export function estimateLine(
  kind: EnvCopyFileKind,
  source: string,
  target: string,
  estimate: EnvCopyEstimate | undefined,
) {
  const found = estimate?.available === true ? estimate.kinds?.find((k) => k.kind === kind) : null;
  if (!found) return 'Size estimate not available.';
  return [
    sideSentence(source, found.source),
    sideSentence(target, found.target),
    moreSentence(found.difference?.bytes),
  ]
    .filter(Boolean)
    .join(' ');
}
